import { useFormikContext } from "formik";
import React, { useState, useRef, useEffect } from "react";

const CustomSelect = ({
  options,
  placeholder,
  selectedOption,
  onChange,
  selectedId,
  onAddNew,
  multipleSelection = false,
}) => {
  const { values, errors, setFieldValue } = useFormikContext();

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(multipleSelection ? [] : null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!multipleSelection) {
      const selectedOpt =
        options?.find((opt) => opt.value === selectedId) || null;

      setSelected(selectedOpt);
    } else {
      if (selectedId) {
        const selectedIds = selectedId.split("||");
        setSelected(options.filter((opt) => selectedIds.includes(opt.value)));
      } else {
        setSelected([]);
      }
    }
  }, [selectedId, multipleSelection, options]);

  const handleOptionChange = (option) => {
    if (multipleSelection) {
      const isSelected = selected.some((item) => item.value === option.value);

      let newSelected = isSelected
        ? selected.filter((item) => item.value !== option.value)
        : [...selected, option];
      setSelected(newSelected);
      setSearchTerm("");

      onChange({
        value: newSelected.map((item) => item.value).join("||"),
        label: newSelected.map((item) => item.label).join(", "),
      });
    } else {
      setSelected(option);
      setSearchTerm("");
      onChange(option);
      setIsOpen(false);
    }
  };

  const handleRemoveSelected = (value, e) => {
    e.stopPropagation();
    const newSelected = selected.filter((item) => item.value !== value);
    setSelected(newSelected);
    onChange({
      value: newSelected.map((item) => item.value).join("||"),
      label: newSelected.map((item) => item.label).join(", "),
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const filteredOptions = options?.filter((option) =>
    option?.label?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (isOpen && ref?.current && !ref?.current.contains(e?.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", checkIfClickedOutside);

    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [isOpen]);

  const handleFocus = () => {
    setIsFocused(true);
    setIsOpen(true);
  };

  const handleBlur = () => {
    if (!searchTerm && !selected) {
      setIsFocused(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <style>
        {`
          .custom-select-chip-scroll::-webkit-scrollbar {
            height: 4px;
          }
          .custom-select-chip-scroll::-webkit-scrollbar-thumb {
            background: #c7c7c7;
            border-radius: 999px;
          }
          .custom-select-chip-scroll::-webkit-scrollbar-track {
            background: transparent;
          }
        `}
      </style>
      <div
        className={`flex justify-between items-center rounded-md border border-gray-300 px-2 cursor-pointer ${
          multipleSelection ? "h-[38px] py-1" : "h-[38px]"
        }`}
        onClick={() => setIsOpen(true)}
      >
        <div
          className={`flex items-center gap-1 w-full min-w-0 h-full ${
            multipleSelection
              ? "flex-nowrap overflow-x-auto overflow-y-hidden pr-2 custom-select-chip-scroll"
              : ""
          }`}
        >
          {/* MULTIPLE SELECTION */}
          {multipleSelection &&
            Array.isArray(selected) &&
            selected.map((item) => (
              <span
                key={item.value}
                className="inline-flex items-center flex-shrink-0 max-w-[220px] bg-gray-200 rounded-full px-2 py-1 text-xs whitespace-nowrap"
              >
                <span className="truncate max-w-[180px]">{item.label}</span>
                <button
                  type="button"
                  onClick={(e) => handleRemoveSelected(item.value, e)}
                  className="ml-1 text-gray-500 hover:text-gray-700"
                >
                  x
                </button>
              </span>
            ))}

          {/* INPUT (shared for both modes) */}
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={handleFocus}
            placeholder={
              multipleSelection
                ? selected?.length === 0
                  ? placeholder
                  : ""
                : selected
                ? selected.label
                : placeholder
            }
            className="p-1 min-w-[90px] border-none focus:outline-none focus:ring-0 text-sm flex-grow bg-transparent"
          />
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
          className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ml-1"
        >
          <img
            src="https://storagereponeevaydevcdn.blob.core.windows.net/business/create_rfq_dropdown_arrow.svg"
            alt="arrow"
            className={`transition-transform duration-200 ease-in-out ${
              isOpen ? "transform rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {isOpen && (
        <div className="absolute left-0 w-full mt-1 bg-white rounded-md border border-gray-300 shadow-md z-10 max-h-80 overflow-auto py-2">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`py-1 px-3 cursor-pointer text-sm hover:bg-gray-100 flex items-center ${
                  (!multipleSelection && option.value === selectedId) ||
                  (multipleSelection &&
                    selected?.some((item) => item.value === option.value))
                    ? "bg-gray-200"
                    : ""
                }`}
                onClick={() => handleOptionChange(option)}
                role="button"
                tabIndex={0}
              >
                {multipleSelection && (
                  <input
                    type="checkbox"
                    checked={selected?.some(
                      (item) => item.value === option.value
                    )}
                    readOnly
                    className="mr-2 mb-4"
                  />
                )}
                <div>
                  <h3 className="font-medium">{option.label}</h3>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="p-4 text-gray-500 text-sm">
              No options found{" "}
              {onAddNew && (
                <span
                  className="text-sm"
                  style={{
                    color: "blue",
                    textDecoration: "underline",
                    fontStyle: "italic",
                    cursor: "pointer",
                  }}
                  onClick={onAddNew}
                >
                  Click to add new project
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
